/**
 * Global keyboard shortcuts.
 *
 * Mount once near the top of the app tree. Captures keydown on window and
 * dispatches to navigation / event hooks:
 *
 *   ⌘ / Ctrl + B   → toggle the collapsible sidebar (writes the same
 *                    `tc.sidebar.collapsed` key the Sidebar reads, then
 *                    fires a `storage`-like custom event so the Sidebar
 *                    updates without a page reload).
 *   ⌘ / Ctrl + K   → open Global Search modal (custom event `tc:open-search`).
 *   ⌘ / Ctrl + N   → new trip       → /quotes/new
 *   ⌘ / Ctrl + L   → new lead       → /leads/new (placeholder lands on
 *                                     /leads until the Leads system ships).
 *   N              → new trip — fires only when focus is NOT in an input/
 *                    textarea/contentEditable.
 *   ?              → open the shortcuts help modal (event `tc:open-shortcuts-help`).
 *
 * Shortcut handling deliberately lives outside React state so it works
 * across re-renders. Public API is exposed via window CustomEvents so other
 * components (Sidebar, GlobalSearch, ShortcutsHelpDialog) can listen and
 * react without prop-drilling.
 */
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const SIDEBAR_KEY = 'tc.sidebar.collapsed';

const fireSidebarToggle = () => {
  try {
    const current = localStorage.getItem(SIDEBAR_KEY) === '1';
    localStorage.setItem(SIDEBAR_KEY, current ? '0' : '1');
  } catch {}
  window.dispatchEvent(new CustomEvent('tc:sidebar-toggle'));
};

const fireOpenSearch = () => window.dispatchEvent(new CustomEvent('tc:open-search'));
const fireOpenShortcutsHelp = () => window.dispatchEvent(new CustomEvent('tc:open-shortcuts-help'));

// Inputs that we should NOT hijack single-letter shortcuts from.
const isEditableTarget = (el) => {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  return false;
};

const KeyboardShortcuts = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onKeyDown = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      const key = (e.key || '').toLowerCase();

      // ⌘/Ctrl + B → toggle sidebar
      if (mod && key === 'b') {
        e.preventDefault();
        fireSidebarToggle();
        return;
      }
      // ⌘/Ctrl + K → open search
      if (mod && key === 'k') {
        e.preventDefault();
        fireOpenSearch();
        return;
      }
      // ⌘/Ctrl + N → new trip
      if (mod && key === 'n') {
        e.preventDefault();
        navigate('/quotes/new');
        return;
      }
      // ⌘/Ctrl + L → new lead (placeholder routes to /leads)
      if (mod && key === 'l') {
        e.preventDefault();
        navigate('/leads');
        return;
      }
      // "?" → open shortcuts help (Shift+/ on US keyboards)
      if (!mod && (key === '?' || (e.shiftKey && key === '/'))) {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        fireOpenShortcutsHelp();
        return;
      }
      // "n" alone → new trip (only when not in an input)
      if (!mod && key === 'n' && !e.shiftKey && !e.altKey) {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        navigate('/quotes/new');
        return;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, location.pathname]);

  return null;
};

export default KeyboardShortcuts;
